import { setUiTextHost } from '@hprofits/ui';
import { createI18n } from 'vue-i18n';

import type { AppLocale, AppMessageSchema } from './messages';
import { DEFAULT_LOCALE, isAppLocale, loadLocaleMessages, messages } from './messages';

const LOCALE_STORAGE_KEY = 'hp-locale';

const readStoredLocale = (): AppLocale | undefined => {
  try {
    const stored = globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY);
    return isAppLocale(stored) ? stored : undefined;
  } catch {
    // Private mode or blocked site data — fall back to the build-time language.
    return undefined;
  }
};

const resolveInitialLocale = (): AppLocale => {
  const stored = readStoredLocale();
  if (stored) {
    return stored;
  }
  return isAppLocale(process.env.LANGUAGE) ? process.env.LANGUAGE : DEFAULT_LOCALE;
};

const RU_FULL_FORM_COUNT = 4;
const TEEN_RANGE_START = 10;
const TEEN_RANGE_END = 20;
const FEW_RANGE_START = 2;
const FEW_RANGE_END = 4;
const DECIMAL_BASE = 10;
const HUNDRED_BASE = 100;

/**
 * 11-19 take the many-form whatever their last digit is, so they are excluded first.
 *
 * The test is on the last TWO digits, not on the number: 111 ends in 1 but is still a teen and
 * takes the many-form. Checking the whole number made every hundred-and-teen — 111 through 114,
 * 211 through 214, and so on — pick the one- or few-form instead.
 */
const isTeen = (choice: number): boolean => {
  const mod100 = choice % HUNDRED_BASE;
  return mod100 > TEEN_RANGE_START && mod100 < TEEN_RANGE_END;
};

const ruPluralIndex = (rawChoice: number, choicesLength: number): number => {
  // A count is a magnitude; a negative one selects the same form as its absolute value rather than
  // falling through every digit test to the many-form.
  const choice = Math.abs(rawChoice);
  const lastDigit = choice % DECIMAL_BASE;
  const notTeen = !isTeen(choice);
  const isOne = notTeen && lastDigit === 1;

  // A message written with fewer than four forms has no separate few-form, so everything that
  // is not a one-form collapses into the second slot.
  if (choicesLength < RU_FULL_FORM_COUNT) {
    return isOne ? 0 : 1;
  }

  // Only the four-form shape carries a dedicated zero-message, which is why this sits below the
  // arity guard: in a two-form message zero is not a one-form and belongs in the second slot.
  if (rawChoice === 0) {
    return 0;
  }

  if (isOne) {
    return 1;
  }
  const isFew = notTeen && lastDigit >= FEW_RANGE_START && lastDigit <= FEW_RANGE_END;
  return isFew ? 2 : 3;
};

const i18n = createI18n<AppMessageSchema, AppLocale, false>({
  legacy: false,
  globalInjection: true,
  locale: resolveInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  // vue-i18n wants every locale in the union named here, so `ru` is declared empty and filled by
  // `ensureLocaleMessages` once its chunk arrives; until then `fallbackLocale` serves the English
  // copy, which is the same thing that happens today for the legacy flat keys `ru` never carries.
  messages: { ...messages, ru: {} },
  missingWarn: false,
  fallbackWarn: false,
  // Hint copy carries deliberate markup (`code` spans, <br>, <strong>, <a>) that HintContent
  // renders through its own whitelist parser instead of v-html, so vue-i18n's blanket
  // "HTML in message" warning fires on every hint and tells us nothing.
  warnHtmlMessage: false,
  // Off on purpose. Escaping is only meaningful when a message reaches `v-html`, and none does:
  // hints render through HintContent, which rebuilds VNodes from a tag whitelist rather than
  // injecting the string. With it on, every plain-text interpolation carrying a quote, apostrophe
  // or ampersand — a domain, an operator name, a server error message — renders as `&quot;` /
  // `&#39;` / `&amp;` on screen.
  escapeParameter: false,
  // `pluralRules`, not `pluralizationRules`: the latter is the legacy-mode alias and is ignored
  // under `legacy: false`, which is how the Russian rule sat inert in the original i18n.js.
  pluralRules: { ru: ruPluralIndex },
});

export const { t, te } = i18n.global;

// Headless code in @hprofits/ui and ui-smart-controls builds label getters at module scope, where
// no component instance exists for `useUiText` to read `$t` from. Announcing the translator here
// is what makes those labels follow the locale instead of freezing on the English fallback.
setUiTextHost((key, named) => t(key, named ?? {}));

/**
 * `t` returns the key itself when a message is missing, so a caller that treats "no copy" as
 * "render nothing" would put `modules.campaigns…` on screen instead. Callers that need the absent
 * case — an optional tooltip, a `?? serverDescription` fallback — use this.
 */
export const tOptional = (key: string): string | undefined => (te(key) ? t(key) : undefined);

/**
 * The document's own language, which is not the same thing as the copy on screen.
 *
 * Screen readers pick a voice from it, browsers pick spellcheck and hyphenation dictionaries from
 * it, and translation prompts key off it. Left at the build-time `en` while the UI renders Russian,
 * every one of those is wrong — and none of it is visible to a sighted developer, which is why it
 * survived this long.
 */
const applyDocumentLocale = (locale: AppLocale): void => {
  globalThis.document?.documentElement?.setAttribute('lang', locale);
};

applyDocumentLocale(i18n.global.locale.value);

// `availableLocales` cannot answer this: every locale in the union is present from the start, `ru`
// merely empty (see the `messages` option above).
const registeredLocales = new Set<AppLocale>([DEFAULT_LOCALE]);

/**
 * Only `en` ships in the entry chunk (see `messages.ts`), so every other locale has to be fetched
 * before it can be selected — switching to a locale whose catalogue has not arrived yet would
 * render the English fallback and then swap the whole screen once the chunk landed.
 */
export const ensureLocaleMessages = async (locale: AppLocale): Promise<void> => {
  if (registeredLocales.has(locale)) {
    return;
  }
  i18n.global.setLocaleMessage(locale, await loadLocaleMessages(locale));
  registeredLocales.add(locale);
};

const persistLocale = (locale: AppLocale): void => {
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Nothing to do — the choice just will not survive a reload.
  }
};

/**
 * The locale the app is HEADING for, which is not `i18n.global.locale` until the catalogue lands.
 *
 * `setLocale` awaits a chunk fetch and every switcher entry stays clickable while it is in flight,
 * so two clicks overlap: without this, the second click compares itself against the still-applied
 * locale, decides there is nothing to do, and the first request wins on arrival — leaving the user
 * in the language they just cancelled, persisted.
 */
let targetLocale: AppLocale = i18n.global.locale.value;

export const getTargetLocale = (): AppLocale => targetLocale;

/**
 * A catalogue chunk that never arrives costs the locale, not just its messages. Left selected, the
 * locale renders English through `fallbackLocale` while `<html lang>` still claims the missing
 * language and the switcher marks it as the current one — which, since the current entry is
 * disabled, is also the one way back to it. Falling the locale itself back keeps the three in
 * agreement and leaves the retry one click away.
 *
 * The stored choice falls back with them. Left pointing at the failed locale it makes every
 * subsequent reload re-fetch the same missing chunk, and it cannot be corrected from the UI: the
 * app is already showing the default, so selecting it is a no-op that never reaches storage.
 */
export const fallBackToDefaultLocale = (): void => {
  targetLocale = DEFAULT_LOCALE;
  i18n.global.locale.value = DEFAULT_LOCALE;
  applyDocumentLocale(DEFAULT_LOCALE);
  persistLocale(DEFAULT_LOCALE);
};

export const setLocale = async (locale: AppLocale): Promise<void> => {
  targetLocale = locale;
  await ensureLocaleMessages(locale);
  // A newer request (or a load failure) took over while the chunk was in flight; that one owns the
  // locale now, and applying this one would undo it.
  if (targetLocale !== locale) {
    return;
  }
  i18n.global.locale.value = locale;
  applyDocumentLocale(locale);
  persistLocale(locale);
};

export default i18n;
